import React, { useState, useEffect } from 'react';

const API = 'https://groovie-business-api-production.up.railway.app/api/v1';

// Occasions offered in the form (mirror the backend template library; 'custom' airs the operator's line).
const OCCASIONS = [
  { value: 'birthday', label: '🎂 Birthday' },
  { value: 'anniversary', label: '💍 Anniversary' },
  { value: 'congrats', label: '🎉 Congratulations' },
  { value: 'welcome', label: '👋 Welcome' },
  { value: 'custom', label: '✍️ Custom message' },
];

// Warmth/energy voices from the shared ElevenLabs set.
const VOICES = [
  { value: 'friendly', label: 'DJ Kira — warm & friendly' },
  { value: 'energetic', label: 'DJ Mike — bright & energetic' },
  { value: 'professional', label: 'DJ Sarah — warm & clear' },
];

// Three-tier delivery (Immediate is owner/admin-only; the backend enforces it).
const DELIVERY = [
  { value: 'scheduled', label: 'Scheduled', desc: 'Airs on the station’s rotation' },
  { value: 'next_break', label: 'Next break', desc: 'Plays at the next natural break' },
  { value: 'immediate', label: 'Immediate', desc: 'Interrupts and plays now (owner/admin)' },
];

const inputStyle = {
  width: '100%', padding: '11px 14px', borderRadius: 10, border: '1.5px solid #e5e7eb',
  fontSize: 14, fontFamily: "'Outfit', sans-serif", color: '#111827', boxSizing: 'border-box', outline: 'none',
};
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };

export default function Shoutouts() {
  const [shoutouts, setShoutouts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({
    channel_id: '', recipient: '', occasion: 'birthday', custom_line: '',
    custom_wrap: false, voice_style: 'friendly', delivery: 'scheduled',
  });
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchChannels(); fetchShoutouts(); }, []);

  const fetchChannels = async () => {
    try {
      const res = await fetch(`${API}/channels`, { headers });
      if (res.ok) {
        const data = await res.json();
        const chs = data.channels || [];
        setChannels(chs);
        if (chs.length && !form.channel_id) setForm(f => ({ ...f, channel_id: String(chs[0].id) }));
      }
    } catch (e) { console.warn('channels', e); }
  };

  const fetchShoutouts = async () => {
    try {
      const res = await fetch(`${API}/shoutouts`, { headers });
      if (res.ok) { const data = await res.json(); setShoutouts(data.shoutouts || []); }
    } catch (e) { console.warn('shoutouts', e); }
    setLoading(false);
  };

  const isCustom = form.occasion === 'custom';
  const canSubmit = form.channel_id && form.recipient.trim() && (!isCustom || form.custom_line.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API}/shoutouts`, {
        method: 'POST', headers,
        body: JSON.stringify({
          channel_id: parseInt(form.channel_id),
          recipient: form.recipient.trim(),
          occasion: form.occasion,
          custom_line: form.custom_line.trim() || null,
          custom_wrap: form.custom_wrap,
          voice_style: form.voice_style,
          delivery: form.delivery,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to create shout-out');
      setMsg({ type: 'ok', text: `Shout out for ${data.shoutout?.recipient} created — “${data.shoutout?.content}”` });
      setForm(f => ({ ...f, recipient: '', custom_line: '' }));
      fetchShoutouts();
    } catch (err) {
      setMsg({ type: 'err', text: err.message });
    } finally { setSaving(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1100, margin: '0 auto', fontFamily: "'Outfit', 'DM Sans', sans-serif" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.5px' }}>DJ Shout Outs</h1>
        <p style={{ fontSize: 14, color: '#9ca3af', marginTop: 4 }}>
          Pick a virtual host voice (AI) — it voices your shout-out and airs on your station.
        </p>
      </div>

      {/* Create form */}
      <form onSubmit={handleSubmit} style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: 24, marginBottom: 28 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Station</label>
            <select value={form.channel_id} onChange={(e) => set('channel_id', e.target.value)} style={inputStyle}>
              {channels.length === 0 && <option value="">No stations</option>}
              {channels.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Recipient name <span style={{ color: '#ef4444' }}>*</span></label>
            <input value={form.recipient} onChange={(e) => set('recipient', e.target.value)} placeholder="e.g. Maria" style={inputStyle} maxLength={60} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Occasion</label>
            <select value={form.occasion} onChange={(e) => set('occasion', e.target.value)} style={inputStyle}>
              {OCCASIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Virtual host voice (AI)</label>
            <select value={form.voice_style} onChange={(e) => set('voice_style', e.target.value)} style={inputStyle}>
              {VOICES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>
            {isCustom ? <>Custom message <span style={{ color: '#ef4444' }}>*</span></> : 'Personal line (optional)'}
          </label>
          <input value={form.custom_line} onChange={(e) => set('custom_line', e.target.value)} maxLength={120}
            placeholder={isCustom ? 'Your message, aired as written…' : 'Add a personal touch (optional)…'} style={inputStyle} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>{form.custom_line.length}/120</span>
            {isCustom && (
              <label style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.custom_wrap} onChange={(e) => set('custom_wrap', e.target.checked)} />
                Add “A special shout out —” intro
              </label>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Delivery</label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {DELIVERY.map(d => (
              <button type="button" key={d.value} onClick={() => set('delivery', d.value)} title={d.desc}
                style={{
                  padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  border: form.delivery === d.value ? '2px solid #7c3aed' : '1.5px solid #e5e7eb',
                  background: form.delivery === d.value ? '#f5f3ff' : '#fff',
                  color: form.delivery === d.value ? '#6b21a8' : '#6b7280',
                }}>
                {d.label}
              </button>
            ))}
          </div>
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>{DELIVERY.find(d => d.value === form.delivery)?.desc}</p>
        </div>

        {msg && (
          <div style={{
            padding: '10px 14px', borderRadius: 10, marginBottom: 16, fontSize: 13,
            background: msg.type === 'ok' ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${msg.type === 'ok' ? '#bbf7d0' : '#fecaca'}`,
            color: msg.type === 'ok' ? '#166534' : '#991b1b',
          }}>{msg.text}</div>
        )}

        <button type="submit" disabled={!canSubmit || saving} style={{
          background: canSubmit && !saving ? 'linear-gradient(135deg, #7c3aed, #a855f7)' : '#e5e7eb',
          color: canSubmit && !saving ? '#fff' : '#9ca3af',
          border: 'none', borderRadius: 12, padding: '12px 28px', fontSize: 14, fontWeight: 600,
          cursor: canSubmit && !saving ? 'pointer' : 'not-allowed', fontFamily: "'Outfit', sans-serif",
        }}>
          {saving ? 'Creating…' : '🎙️ Send Shout Out'}
        </button>
      </form>

      {/* Recent shout-outs */}
      <h2 style={{ fontSize: 16, fontWeight: 700, color: '#374151', margin: '0 0 12px' }}>Recent shout-outs</h2>
      {loading ? (
        <p style={{ color: '#9ca3af', fontSize: 14 }}>Loading…</p>
      ) : shoutouts.length === 0 ? (
        <div style={{ background: '#fafafa', border: '1px dashed #e5e7eb', borderRadius: 12, padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
          No shout-outs yet — create your first one above.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {shoutouts.map((s, i) => (
            <div key={s.id || i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ color: '#111827', fontWeight: 600, margin: '0 0 4px', fontSize: 14 }}>
                  {s.meta?.recipient ? `${s.meta.recipient}` : (s.title || `Shout-out #${i + 1}`)}
                  {s.meta?.occasion && <span style={{ fontSize: 12, color: '#7c3aed', fontWeight: 600, marginLeft: 8 }}>{s.meta.occasion}</span>}
                </p>
                <p style={{ color: '#6b7280', fontSize: 13, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.content}</p>
              </div>
              {s.audio_url && s.audio_url.startsWith('http') && (
                <audio controls src={s.audio_url} style={{ height: 34, flexShrink: 0 }} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
